import { getConfigValue } from "@rumble-it/common-lib/src/config";
import axios, { type AxiosError } from "axios";
import { authenticate } from "../api/authenticate";
import { getAffiliateId } from "../helpers/affiliates";
import { MIME_TYPE_JSON_LD } from "@rumble-it/common-lib/src/constants/mimeTypes";

const axiosInstance = axios.create({
  headers: {
    Accept: MIME_TYPE_JSON_LD,
    "Content-Type": MIME_TYPE_JSON_LD,
  },
});

axiosInstance.interceptors.request.use(async (config) => {
  let token = localStorage.getItem("accessToken");

  if (token === null) {
    token = await authenticate();
  }

  const affiliateId = await getAffiliateId(token);

  if (affiliateId === null) {
    throw new Error("Failed to get affiliate ID.");
  }

  config.headers.Authorization = `Bearer ${token}`;
  config.baseURL = `${getConfigValue("apiUrl")}/affiliates/${affiliateId}`;

  return config;
});

// Intercept responses to return only the data and retry on 401 errors
// If a 401 error occurs, re-authenticate and retry the request
axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data["@type"] === "Collection") {
      response.data = response.data["member"] || [];
    }

    return response;
  },
  async (error: AxiosError) => {
    const status = error.response ? error.response.status : null;
    const config = error.config;

    if (status === 401 && config && !config.isRetryRequest) {
      config.isRetryRequest = true; // Mark the request as a retry

      const token = await authenticate();

      config.headers.Authorization = `Bearer ${token}`;

      return await axiosInstance(config);
    }

    console.error("Request failed:", error);
    return Promise.reject(error);
  },
);

export { axiosInstance };
